import {
  AutopilotTaskDto,
  TaskCategoryStage,
  TaskCategoryWorkflowType,
} from '@urbanx/agx.activities.client';
import {
  UploadedDocumentInfoDocumentType,
  UploadedDocumentInfoSigningMethod,
  useStaffGetDocumentsForCampaign,
} from '@urbanx/agx.campaigns.client';
import { AuthContext } from '@urbanx/agx.auth.client';
import { cleanFullAddress } from '@urbanx/agx.ui-components2';
import { MenuTitles } from 'constants/menu';
import { useSelectedAgency, useSelectedCampaign } from 'hooks';
import { ContentLayout, PageLayout } from 'layout';
import { useCallback, useContext, useMemo, useState } from 'react';
import AgreementCompliance from './AgreementCompliance/AgreementCompliance';
import Tasks, { TaskTabs } from 'components/tasks/Tasks';
import TaskDueDateBadge from 'components/tasks/TaskDueDateBadge';
import { getStepsCompletionInfo } from 'utils/tasks';
import WithdrawListingAction from '../WithdrawListingAction';
import TaskCompletionConfirmationModal from 'pages/allTasks/TaskModal/TaskCompletionConfirmationModal';
import { getInteractiveAgreementComplianceTask } from 'pages/allTasks/TaskModal/agreementComplianceTaskLayout';

const CampaignTasks = () => {
  const { userAccount } = useContext(AuthContext);
  const { agency } = useSelectedAgency();
  const { campaign } = useSelectedCampaign();

  const [showComplianceConfirmation, setShowComplianceConfirmation] = useState(false);
  const [allTasks, setAllTasks] = useState<AutopilotTaskDto[]>([]);

  const { data: documentGroups, isPending: documentsPending } = useStaffGetDocumentsForCampaign(
    campaign?.campaignId || '',
    !!campaign?.campaignId
  );

  const agencyAgreementSignedByAgent = useMemo(() => {
    return documentGroups
      ?.flatMap((group) => group.documents ?? [])
      .find(
        (doc) =>
          doc.documentType === UploadedDocumentInfoDocumentType.AgencyAgreement &&
          doc.signingMethod === UploadedDocumentInfoSigningMethod.Agent
      );
  }, [documentGroups]);

  const complianceTask = useMemo(() => {
    return getInteractiveAgreementComplianceTask(allTasks);
  }, [allTasks]);
  const { allStepsCompleted: allComplianceStepsCompleted, latestCompletedDate } = useMemo(
    () => getStepsCompletionInfo(complianceTask ? [complianceTask] : []),
    [complianceTask]
  );

  const handleTasksChange = useCallback((tasks: AutopilotTaskDto[]) => {
    setAllTasks(tasks);
  }, []);

  // Filter out AgreementCompliance tasks from main Tasks list (they're rendered separately)
  const taskFilter = useCallback(
    (task: AutopilotTaskDto) => task.category?.stage !== TaskCategoryStage.AgreementCompliance,
    []
  );

  const renderAdditionalContent = useCallback(
    ({
      selectedTab,
      selectedWorkflowStage,
      allTasksCompleted,
      isViewingCompletedStage,
    }: {
      selectedTab: TaskTabs;
      selectedWorkflowStage?: TaskCategoryStage;
      allTasksCompleted: boolean;
      isViewingCompletedStage: boolean;
    }) => {
      if (selectedTab !== TaskTabs.Tasks) return null;

      const shouldShowCompliance =
        selectedWorkflowStage === TaskCategoryStage.AgreementCompliance ||
        (!allComplianceStepsCompleted &&
          !documentsPending &&
          agencyAgreementSignedByAgent &&
          complianceTask);

      // Don't show compliance when viewing a different completed stage
      if (
        isViewingCompletedStage &&
        selectedWorkflowStage !== TaskCategoryStage.AgreementCompliance
      )
        return null;
      // Don't show when all completed and not viewing a specific stage
      if (allTasksCompleted && !isViewingCompletedStage) return null;
      if (!shouldShowCompliance) return null;

      if (!complianceTask) return null;

      const dueDateValue = allComplianceStepsCompleted
        ? latestCompletedDate
        : complianceTask.dueUtc;
      const dueDate = dueDateValue ? new Date(dueDateValue) : '';

      return (
        <AgreementCompliance
          badge={
            <TaskDueDateBadge
              dueDate={dueDate}
              isCompleted={allComplianceStepsCompleted}
              completedDate={latestCompletedDate}
            />
          }
          task={complianceTask}
          onCompleteSuccess={() => setShowComplianceConfirmation(true)}
        />
      );
    },
    [
      allComplianceStepsCompleted,
      documentsPending,
      agencyAgreementSignedByAgent,
      complianceTask,
      latestCompletedDate,
    ]
  );

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CAMPAIGN_TASKS}
        isCampaigns={true}
        secondaryActions={<WithdrawListingAction />}
      >
        <Tasks
          campaignId={campaign?.campaignId ?? ''}
          primaryWorkflowType={TaskCategoryWorkflowType.Campaign}
          taskFilter={taskFilter}
          onTasksChange={handleTasksChange}
          renderAdditionalContent={renderAdditionalContent}
        />

        <TaskCompletionConfirmationModal
          isOpen={showComplianceConfirmation}
          onOpenChange={setShowComplianceConfirmation}
          title="Agreement Compliance now complete."
          message="Agreement will be emailed to the Vendor(s)."
          autoCloseAfterMs={1000}
          presentation="agreementCompliance"
        />
      </ContentLayout>
    </PageLayout>
  );
};

export default CampaignTasks;
